import { loginViewCopy } from "../app/i18n";
import type { AppLanguage } from "../app/language";

type LoginViewMode = "login" | "request-reset" | "confirm-reset" | "force-reset";

type LoginViewProps = {
  language: AppLanguage;
  mode: LoginViewMode;
  passwordResetEnabled: boolean;
  email: string;
  password: string;
  forcedCurrentPassword: string;
  forcedPasswordError: string | null;
  isUpdatingForcedPassword: boolean;
  newPassword: string;
  confirmPassword: string;
  isSubmitting: boolean;
  isRequestingPasswordReset: boolean;
  isSubmittingPasswordReset: boolean;
  error: string | null;
  resetRequestError: string | null;
  resetRequestNotice: string | null;
  resetSubmitError: string | null;
  resetSubmitNotice: string | null;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onForcedCurrentPasswordChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onUpdateForcedPassword: () => void;
  onRequestPasswordReset: () => void;
  onResetPassword: () => void;
  onShowPasswordReset: () => void;
  onBackToSignIn: () => void;
};

export function LoginView({
  language,
  mode,
  passwordResetEnabled,
  email,
  password,
  forcedCurrentPassword,
  forcedPasswordError,
  isUpdatingForcedPassword,
  newPassword,
  confirmPassword,
  isSubmitting,
  isRequestingPasswordReset,
  isSubmittingPasswordReset,
  error,
  resetRequestError,
  resetRequestNotice,
  resetSubmitError,
  resetSubmitNotice,
  onEmailChange,
  onPasswordChange,
  onForcedCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onUpdateForcedPassword,
  onRequestPasswordReset,
  onResetPassword,
  onShowPasswordReset,
  onBackToSignIn,
}: LoginViewProps) {
  const isLoginMode = mode === "login";
  const isConfirmResetMode = mode === "confirm-reset";
  const isForcedResetMode = mode === "force-reset";
  const copy = loginViewCopy[language];

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f3f3f4] px-6 py-10">
      <section className="tenant-login-shell w-full max-w-[980px] overflow-hidden rounded-[40px] border border-white/70 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
        <div className="grid min-h-[680px] lg:grid-cols-[1.1fr_0.9fr]">
          <div className="brand-primary-panel flex flex-col justify-between px-8 py-10 sm:px-12 sm:py-12">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/70">
                {isLoginMode || isForcedResetMode ? copy.accountAccess : copy.passwordRecovery}
              </p>
              <h1 className="font-headline mt-5 text-4xl font-extrabold tracking-[-0.05em] text-white sm:text-5xl">
                {isLoginMode || isForcedResetMode ? copy.welcomeTitle : copy.recoveryTitle}
              </h1>
              <p className="mt-4 max-w-xl text-base leading-8 text-white/78 sm:text-lg">
                {isLoginMode
                  ? copy.welcomeDescription
                  : isForcedResetMode
                    ? copy.forcedResetWelcomeDescription
                  : copy.recoveryDescription}
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              {copy.featureCards.map(([title, detail]) => (
                <div key={title} className="rounded-[24px] border border-white/15 bg-white/8 p-5 backdrop-blur-sm">
                  <p className="text-sm font-bold text-white">{title}</p>
                  <p className="mt-2 text-sm leading-6 text-white/72">{detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="tenant-login-surface flex items-center bg-[linear-gradient(180deg,#fcfcfd,#f8fafc)] px-6 py-10 sm:px-10 lg:px-12">
            <div className="w-full">
              <div className="mx-auto max-w-md">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">
                  {isLoginMode ? copy.loginEyebrow : isForcedResetMode ? copy.forcedResetEyebrow : isConfirmResetMode ? copy.setNewPasswordEyebrow : copy.resetPasswordEyebrow}
                </p>
                <h2 className="font-headline mt-3 text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">
                  {isLoginMode
                    ? copy.signInTitle
                    : isForcedResetMode
                      ? copy.forcedResetTitle
                      : isConfirmResetMode
                      ? copy.choosePasswordTitle
                      : copy.requestResetTitle}
                </h2>
                <p className="mt-3 text-sm leading-7 text-[#667085]">
                  {isLoginMode
                    ? copy.signInDescription
                    : isForcedResetMode
                      ? copy.forcedResetDescription
                      : isConfirmResetMode
                      ? copy.choosePasswordDescription
                      : copy.requestResetDescription}
                </p>

                {!isLoginMode && !isForcedResetMode ? (
                  <div className="mt-6 inline-flex rounded-full border border-[#d0d5dd] bg-[#f8fafc] p-1">
                    <button
                      type="button"
                      onClick={onBackToSignIn}
                      className="brand-primary-subtle-button rounded-full px-4 py-2 text-sm font-semibold transition"
                    >
                      {copy.backToSignIn}
                    </button>
                  </div>
                ) : null}

                {isLoginMode ? (
                  <form
                    className="mt-8 space-y-5"
                    onSubmit={event => {
                      event.preventDefault();
                      onSubmit();
                    }}
                  >
                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.email}</span>
                      <input
                        type="email"
                        value={email}
                        onChange={event => onEmailChange(event.target.value)}
                        autoComplete="email"
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3]"
                        placeholder={copy.emailPlaceholder}
                      />
                    </label>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.password}</span>
                      <input
                        type="password"
                        value={password}
                        onChange={event => onPasswordChange(event.target.value)}
                        autoComplete="current-password"
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3]"
                        placeholder={copy.passwordPlaceholder}
                      />
                    </label>

                    {passwordResetEnabled ? (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={onShowPasswordReset}
                          className="text-sm font-semibold text-[#006949] transition hover:text-[#00855d]"
                        >
                          {copy.forgotPassword}
                        </button>
                      </div>
                    ) : null}

                    {error ? (
                      <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                        {error}
                      </div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={isSubmitting || !email.trim() || !password}
                      className={`w-full rounded-[22px] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em] transition-all ${
                        isSubmitting || !email.trim() || !password
                          ? "cursor-not-allowed bg-[#eaecf0] text-[#98a2b3]"
                          : "brand-primary-button"
                      }`}
                    >
                      {isSubmitting ? copy.signingIn : copy.signIn}
                    </button>
                  </form>
                ) : isForcedResetMode ? (
                  <form
                    className="mt-8 space-y-5"
                    onSubmit={event => {
                      event.preventDefault();
                      onUpdateForcedPassword();
                    }}
                  >
                    <div className="rounded-[24px] border border-[#d0d5dd] bg-[#f8fafc] px-5 py-4 text-sm leading-7 text-[#475467]">
                      {copy.forcedResetInfo}
                    </div>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.temporaryPassword}</span>
                      <input
                        type="password"
                        value={forcedCurrentPassword}
                        onChange={event => onForcedCurrentPasswordChange(event.target.value)}
                        autoComplete="current-password"
                        disabled={isUpdatingForcedPassword}
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3] disabled:cursor-not-allowed disabled:bg-[#f8fafc]"
                        placeholder={copy.temporaryPasswordPlaceholder}
                      />
                    </label>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.newPassword}</span>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={event => onNewPasswordChange(event.target.value)}
                        autoComplete="new-password"
                        disabled={isUpdatingForcedPassword}
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3] disabled:cursor-not-allowed disabled:bg-[#f8fafc]"
                        placeholder={copy.newPasswordPlaceholder}
                      />
                    </label>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.confirmPassword}</span>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={event => onConfirmPasswordChange(event.target.value)}
                        autoComplete="new-password"
                        disabled={isUpdatingForcedPassword}
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3] disabled:cursor-not-allowed disabled:bg-[#f8fafc]"
                        placeholder={copy.confirmPasswordPlaceholder}
                      />
                    </label>

                    {forcedPasswordError ? (
                      <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                        {forcedPasswordError}
                      </div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={isUpdatingForcedPassword || !forcedCurrentPassword || !newPassword || !confirmPassword}
                      className={`w-full rounded-[22px] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em] transition-all ${
                        isUpdatingForcedPassword || !forcedCurrentPassword || !newPassword || !confirmPassword
                          ? "cursor-not-allowed bg-[#eaecf0] text-[#98a2b3]"
                          : "brand-primary-button"
                      }`}
                    >
                      {isUpdatingForcedPassword ? copy.updatingPassword : copy.updateRequiredPassword}
                    </button>
                  </form>
                ) : isConfirmResetMode ? (
                  <form
                    className="mt-8 space-y-5"
                    onSubmit={event => {
                      event.preventDefault();
                      onResetPassword();
                    }}
                  >
                    <div className="rounded-[24px] border border-[#d0d5dd] bg-[#f8fafc] px-5 py-4 text-sm leading-7 text-[#475467]">
                      {copy.resetInfo}
                    </div>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.newPassword}</span>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={event => onNewPasswordChange(event.target.value)}
                        autoComplete="new-password"
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3]"
                        placeholder={copy.newPasswordPlaceholder}
                      />
                    </label>

                    <label className="block">
                      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.confirmPassword}</span>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={event => onConfirmPasswordChange(event.target.value)}
                        autoComplete="new-password"
                        className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3]"
                        placeholder={copy.confirmPasswordPlaceholder}
                      />
                    </label>

                    {resetSubmitNotice ? (
                      <div className="rounded-[20px] border border-[#abefc6] bg-[#ecfdf3] px-4 py-3 text-sm text-[#067647]">{resetSubmitNotice}</div>
                    ) : null}

                    {resetSubmitError ? (
                      <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">{resetSubmitError}</div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={isSubmittingPasswordReset || !newPassword || !confirmPassword}
                      className={`w-full rounded-[22px] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em] transition-all ${
                        isSubmittingPasswordReset || !newPassword || !confirmPassword
                          ? "cursor-not-allowed bg-[#eaecf0] text-[#98a2b3]"
                          : "brand-primary-button"
                      }`}
                    >
                      {isSubmittingPasswordReset ? copy.updatingPassword : copy.setNewPassword}
                    </button>
                  </form>
                ) : (
                  <form
                    className="mt-8 space-y-5"
                    onSubmit={event => {
                      event.preventDefault();
                      onRequestPasswordReset();
                    }}
                  >
                    {!passwordResetEnabled ? (
                      <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-4 text-sm leading-7 text-[#b42318]">
                        {copy.resetDisabled}
                      </div>
                    ) : (
                      <>
                        <label className="block">
                          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.email}</span>
                          <input
                            type="email"
                            value={email}
                            onChange={event => onEmailChange(event.target.value)}
                            autoComplete="email"
                            className="brand-primary-focus-ring mt-2 w-full rounded-[22px] border border-[#e4e7ec] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none transition placeholder:text-[#98a2b3]"
                            placeholder={copy.emailPlaceholder}
                          />
                        </label>

                        {resetRequestNotice ? (
                          <div className="rounded-[20px] border border-[#abefc6] bg-[#ecfdf3] px-4 py-3 text-sm text-[#067647]">{copy.resetRequestInfo}</div>
                        ) : null}

                        {resetRequestError ? (
                          <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">{resetRequestError}</div>
                        ) : null}

                        {resetSubmitError ? (
                          <div className="rounded-[20px] border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">{resetSubmitError}</div>
                        ) : null}

                        <button
                          type="submit"
                          disabled={isRequestingPasswordReset || !email.trim()}
                          className={`w-full rounded-[22px] px-5 py-3 text-sm font-bold uppercase tracking-[0.2em] transition-all ${
                            isRequestingPasswordReset || !email.trim()
                              ? "cursor-not-allowed bg-[#eaecf0] text-[#98a2b3]"
                              : "brand-primary-button"
                          }`}
                        >
                          {isRequestingPasswordReset ? copy.sendingResetLink : copy.sendResetLink}
                        </button>
                      </>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
